import { makeAutoObservable, runInAction } from 'mobx'

import { format } from 'date-fns/format'
import { fromUnixTime } from 'date-fns/fromUnixTime'
import { getUnixTime } from 'date-fns/getUnixTime'
import { subMonths } from 'date-fns/subMonths'

import { FinstrongApi } from '../api'
import type {
  MonthlySummaries,
  HealthScore,
  HealthScoreReport,
  MonthlyHealthScore,
  Transaction,
} from '../types'
import type { FinstrongTransaction, PeerAverage } from '../types/Finstrong'
import { augmentChartData, augmentSpendingFeeTransactions } from '../utils/FinstrongUtils'

import { GlobalStore } from './GlobalStore'

export class FinstrongStore {
  api = new FinstrongApi('/', '')
  augmentedChartData: { x: string; y: number }[] = []
  averageHealthScores: MonthlyHealthScore[] = []
  monthlySummaries: MonthlySummaries[] = []
  incomeTransactions: FinstrongTransaction[] = []
  debtSpendTransactions: FinstrongTransaction[] = []
  spendingFeeTransactions: Transaction[] = []
  spendTransactions: FinstrongTransaction[] = []
  globalStore: GlobalStore
  healthScore: HealthScore = {} as HealthScore
  healthScoreChange?: number
  healthScoreChangeReports: HealthScoreReport[] = []
  peerScore: PeerAverage | null = null
  isSpendingInitialized: boolean = false
  isIncomeInitialized: boolean = false

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore
    this.api = new FinstrongApi(globalStore.endpoint, globalStore.sessionToken, globalStore.onError)
    makeAutoObservable(this)
  }

  setSpendingInitialized = (isSpendingInitialized: boolean) => {
    this.isSpendingInitialized = isSpendingInitialized
  }

  setIncomeInitialized = (isIncomeInitialized: boolean) => {
    this.isIncomeInitialized = isIncomeInitialized
  }

  calculateHealthScore = async (needHealthScoreChangeReport: boolean = true) => {
    try {
      const healthScore = await this.api.calculateHealthScore()

      let healthScoreChange = 0

      if (needHealthScoreChangeReport) {
        await this.loadHealthScoreChangeReports()
      }

      const currentScore = healthScore?.health_score || 0
      const oldScore = this.healthScoreChangeReports.at(-1)?.older_health_score || 0
      healthScoreChange = currentScore - oldScore

      runInAction(() => {
        this.healthScore = healthScore
        this.healthScoreChange = healthScoreChange
      })
    } catch (e) {
      console.error('Error calculating health score:', e)
      this.globalStore.captureException(e, { store: 'FinstrongStore' })
    }
  }

  loadAverageHealthScores = async () => {
    try {
      const averageHealthScores = await this.api.getAverageHealthScores()
      runInAction(() => {
        this.averageHealthScores = averageHealthScores
      })
    } catch (e) {
      console.error('Error loading average health scores:', e)
      this.globalStore.captureException(e, { store: 'FinstrongStore' })
    }
  }

  loadMonthlySummaries = async () => {
    try {
      const monthlySummaries = await this.api.getMonthlySummaries()
      runInAction(() => {
        this.monthlySummaries = monthlySummaries
      })
    } catch (e) {
      console.error('Error loading monthly summaries:', e)
      this.globalStore.captureException(e, { store: 'FinstrongStore' })
    }
  }

  loadDebtSpend = async () => {
    try {
      const debtSpend = await this.api.getDebtSpend()
      runInAction(() => {
        this.debtSpendTransactions = debtSpend
      })
    } catch (e) {
      console.error('Error loading debt spend:', e)
      this.globalStore.captureException(e, { store: 'FinstrongStore' })
    }
  }

  loadIncome = async () => {
    this.setIncomeInitialized(false)
    try {
      const income = await this.api.getIncome()
      runInAction(() => {
        this.incomeTransactions = income
        this.setIncomeInitialized(true)
      })
    } catch (e) {
      console.error('Error loading income:', e)
      this.globalStore.captureException(e, { store: 'FinstrongStore' })
    }
  }

  loadHealthScoreChangeReports = async () => {
    const startDate = getUnixTime(subMonths(new Date(), 2))
    const endDate = getUnixTime(new Date())

    try {
      const healthScoreChangeReports = await this.api.getHealthScoreChangeReports(
        startDate,
        endDate,
      )
      runInAction(() => {
        this.healthScoreChangeReports = healthScoreChangeReports
      })
    } catch (e) {
      console.error('Error loading health score change reports:', e)
      this.globalStore.captureException(e, { store: 'FinstrongStore' })
    }
  }

  loadPeerScore = async () => {
    const birthYear = format(fromUnixTime(this.globalStore.appDataStore.user.birthday!), 'yyyy')

    try {
      const peerScore = await this.api.getPeerScore(birthYear)
      runInAction(() => {
        this.peerScore = peerScore
      })
    } catch (e) {
      console.error('Error loading peer score:', e)
      this.globalStore.captureException(e, { store: 'FinstrongStore' })
    }
  }

  loadAugmentedChartData = async () => {
    try {
      const augmentedChartData = await this.api.getAverageHealthScores()

      runInAction(() => {
        this.augmentedChartData = augmentChartData(augmentedChartData)
      })
    } catch (error) {
      console.error('Error occurred while getting augmented chart data:', error)
      this.globalStore.captureException(error, { store: 'FinstrongStore' })
    }
  }

  getSpendingFeeTransactions = async () => {
    try {
      const spendingFeeTransaction = await this.api.getSpendingFeeTransactions()
      const organizedSpendingFeeTransactions =
        augmentSpendingFeeTransactions(spendingFeeTransaction)
      runInAction(() => {
        this.spendingFeeTransactions = organizedSpendingFeeTransactions
      })
    } catch (error) {
      console.error('Error occurred while getting spending fee transactions:', error)
      this.globalStore.captureException(error, { store: 'FinstrongStore' })
    }
  }

  loadSpendTransactions = async () => {
    this.setSpendingInitialized(false)
    try {
      const spendTransaction = await this.api.getStandardSpend()
      runInAction(() => {
        this.spendTransactions = [...spendTransaction]
        this.setSpendingInitialized(true)
      })
    } catch (e) {
      console.error('Error loading spend transactions:', e)
      this.globalStore.captureException(e, { store: 'FinstrongStore' })
    }
  }

  get spendAndDebtTransactions() {
    return [...this.spendTransactions, ...this.debtSpendTransactions]
  }

  get allTransactions() {
    return {
      income: this.incomeTransactions,
      debtSpend: this.debtSpendTransactions,
      spendingFee: this.spendingFeeTransactions,
      spend: this.spendTransactions,
    }
  }
  handleFinstrongWidgetPrimaryCtaClick = (value: string) => () => {
    // eslint-disable-next-line no-void
    void value
  }
}
